import { HttpRequest } from "../../Pages/httpRequest";
import { IssueLogger } from "../Root/deferred/issueLogger";
import { IProductAvailability } from "./IProductAvailability";

export class CategoryProduct {
    private innerProductContainer = document.getElementById("category-image-scroller") as HTMLDivElement;

    constructor() {
        this.wireUpProductMouseOvers();
    }

    private swapToAltImage(img: HTMLImageElement, previousPrefix: string, nextPrefix: string): void {
        if (img) {
            let oldImageId = img.dataset[previousPrefix + "Image"];
            let oldImageVersion = img.dataset[previousPrefix + "Version"];
            let oldImageSuffix = "/v" + oldImageVersion + "/product/" + oldImageId + ".jpg";
            let newImageId = img.dataset[nextPrefix + "Image"];
            let newImageVersion = img.dataset[nextPrefix + "Version"];
            let newImageSuffix = "/v" + newImageVersion + "/product/" + newImageId + ".jpg";
            let src = img.getAttribute("src").replace(oldImageSuffix, newImageSuffix);

            img.setAttribute("src", src);
        }
    }

    private wireUpProductMouseOvers() {
        if (window.innerWidth <= 1024 || !this.innerProductContainer) {
            return;
        }

        this.innerProductContainer.addEventListener("mouseover", (e) => this.onProductContainerMouseEnter(e));
        this.innerProductContainer.addEventListener("mouseout", (e) => this.onProductCategoryImageMouseLeave(e));
    }

    private onProductContainerMouseEnter(e: MouseEvent): void {
        if (!e.target) {
            return;
        }

        const container = (e.target as Element).closest('.product-img');
        if (!container) {
            return;
        }

        let img = container.querySelector<HTMLImageElement>("img.item-image");
        let sizeDiv = container.querySelector<HTMLDivElement>(".product-mouseover");

        if (!sizeDiv.dataset.status) {
            sizeDiv.dataset.status = "loading";
            const styleId = sizeDiv.dataset.id;
            const colourId = sizeDiv.dataset.colourId;
            this.createStockMessageHtml(sizeDiv, "Checking Availability");
            this.createQuickViewLinkHtml(sizeDiv);
            HttpRequest.Get(`/ws/availability/${styleId}/${colourId}`, "json",
                (data) => { this.onAvailabilityReceived(sizeDiv, data); },
                () => { this.onAvailabilityError(sizeDiv) },
            );
        }

        sizeDiv.classList.remove("hidden");
        this.swapToAltImage(img, "main", "alt");
    }

    private onProductCategoryImageMouseLeave(e: MouseEvent): void {
        if (!e.target) {
            return;
        }

        const container = (e.target as Element).closest('.product-img');
        if (!container) {
            return;
        }

        let img = container.querySelector<HTMLImageElement>("img.item-image");
        let sizeDiv = container.querySelector<HTMLDivElement>(".product-mouseover");

        if (sizeDiv) {
            sizeDiv.classList.add("hidden");
        }

        this.swapToAltImage(img, "alt", "main");
    }

    private createStockMessageHtml(sizeContainer: HTMLElement, message: string): void {
        let messageDivs = sizeContainer.getElementsByClassName("sizes-mouseover");
        let messageDiv: HTMLDivElement;
        let messageHeading: HTMLElement;

        if (messageDivs.length === 0) {
            // Create the <div><h3>message structure, first time called.
            messageDiv = document.createElement("div");
            messageDiv.className = "sizes-mouseover";

            messageHeading = document.createElement("h3");
            messageHeading.appendChild(document.createTextNode(message));

            messageDiv.appendChild(messageHeading);

            sizeContainer.innerHTML = "";
            sizeContainer.appendChild(messageDiv);
        }
        else {
            // Structure already exists, update the message.
            messageDiv = <HTMLDivElement>messageDivs[0];
            messageHeading = <HTMLElement>messageDiv.children[0];
            messageHeading.innerText = message;
        }
    }

    private createStockAvailabilityHtml(sizeContainer: HTMLElement, products: IProductAvailability[]): void {
        // At this point the div already exists as it was used to display the 'loading' text.
        let innerSizesContainer = sizeContainer.getElementsByClassName("sizes-mouseover")[0];

        innerSizesContainer.innerHTML = ""; // Clear out the 'loading' message.
        innerSizesContainer.className = "sizes-mouseover";

        // H3 static header BEGINS.
        let sizesHeading = document.createElement("h3");
        sizesHeading.appendChild(document.createTextNode("Sizes Available "));

        sizesHeading.appendChild(this.createStockSpan("InStock", "fa fa-check"));
        sizesHeading.appendChild(this.createStockSpan("SoldOut", "fa fa-close"));

        innerSizesContainer.appendChild(sizesHeading);
        // Insert at the start so we do not disturb the layout where the existing QuickView link is at the bottom.
        sizeContainer.insertBefore(innerSizesContainer, sizeContainer.firstChild);
        // H3 static header ENDS.

        let url = sizeContainer.dataset.url;

        // The list of sizes.
        for (let product of products) {
            let sizeLink = document.createElement("a");

            sizeLink.href = url + "&s=" + encodeURI(product.sizePosCode);

            let span = document.createElement("span");

            span.className = product.availability;
            span.innerText = product.size;

            sizeLink.appendChild(span);
            innerSizesContainer.appendChild(sizeLink);
        }

        sizeContainer.dataset.status = "loaded";
    }

    private createQuickViewLinkHtml(sizeContainer: HTMLElement): void {
        let quickViewLink = <HTMLAnchorElement>(sizeContainer.getElementsByClassName("QuickAdd_Show")[0]);

        if (quickViewLink) {
            return;
        }
        quickViewLink = document.createElement("a");

        let url = sizeContainer.dataset.url;
        let styleId = sizeContainer.dataset.id;
        let categoryId = sizeContainer.dataset.category;

        quickViewLink.className = "no-text icon-info QuickAdd_Show";
        quickViewLink.href = url;
        quickViewLink.dataset.id = styleId;
        quickViewLink.dataset.category = categoryId;
        quickViewLink.innerText = "Info";

        sizeContainer.appendChild(quickViewLink);
    }

    private createStockSpan(spanClassName: string, glyphClassName: string): HTMLElement {
        let span = document.createElement("span");

        span.className = spanClassName;
        span.appendChild(this.createGlyph(glyphClassName));

        return span;
    }

    private createGlyph(className: string): HTMLElement {
        let glyph = document.createElement("i");
        glyph.className = className;
        glyph.ariaHidden = "true";
        return glyph;
    }

    private onAvailabilityReceived(sizeContainer: HTMLElement, request: XMLHttpRequest): void {
        if (request?.response) {
            this.createStockAvailabilityHtml(sizeContainer, request.response);
        }
        else {
            this.onAvailabilityError(sizeContainer)
        }
    }

    private onAvailabilityError(sizeContainer: HTMLElement): void {
        this.createStockMessageHtml(sizeContainer, "Unable to check stock availability. Please try again.");
        IssueLogger.log("Availability unable to be loaded for category product.", "category-product");
        delete sizeContainer.dataset.status; // Enable a retry of lookup by clearing the flag.
    }
}
